import React, { useState, useEffect } from 'react';
import { API_CONFIG } from '../../config/api';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Save, Eye, Sparkles, FileText, Settings, RefreshCw } from 'lucide-react';
import { supabase } from '../../lib/supabase';

interface NewsArticle {
  id: number;
  title: string;
  category?: string;
}

interface AnalysisTemplate {
  id: number;
  name: string;
}

interface OfficialAnalysis {
  id: number;
  article_id: number;
  template_id: number;
  author_id: number;
  content_json: any;
  full_article?: string;
  slug?: string;
  status: number;
  created_at: string;
  updated_at: string;
  article?: NewsArticle;
  template?: AnalysisTemplate;
}

interface FormData {
  article_id: number;
  template_id: number;
  content_json: string;
  full_article: string;
  slug: string;
  status: number;
}

const OfficialAnalysisEdit: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEditing = id !== 'new';
  
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [skipAutoSave, setSkipAutoSave] = useState(false);
  const [articles, setArticles] = useState<NewsArticle[]>([]);
  const [templates, setTemplates] = useState<AnalysisTemplate[]>([]);
  const [analysis, setAnalysis] = useState<OfficialAnalysis | null>(null);
  const [activeTab, setActiveTab] = useState<'basic' | 'article'>('basic');
  const [autoSaving, setAutoSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  
  const [formData, setFormData] = useState<FormData>({
    article_id: 0,
    template_id: 0,
    content_json: '',
    full_article: '',
    slug: '',
    status: 0
  });

  // 获取文章列表
  const fetchArticles = async () => {
    try {
      // 获取当前用户的 session token
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      if (sessionError || !session?.access_token) {
        console.error('获取 session 失败:', sessionError);
        return;
      }

      // 调用 Edge Function 获取文章列表
      const functionsUrl = API_CONFIG.FUNCTIONS_URL || `${import.meta.env.VITE_SUPABASE_URL}/functions/v1`;
      const response = await fetch(`${functionsUrl}/news-admin-manage?module=analysis&action=get_articles`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        }
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          setArticles(result.data || []);
        }
      }
    } catch (error) {
      console.error('获取文章列表失败:', error);
    }
  };

  // 获取模板列表
  const fetchTemplates = async () => {
    try {
      // 获取当前用户的 session token
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      if (sessionError || !session?.access_token) {
        console.error('获取 session 失败:', sessionError);
        return;
      }

      // 调用 Edge Function 获取模板列表
      const functionsUrl = API_CONFIG.FUNCTIONS_URL || `${import.meta.env.VITE_SUPABASE_URL}/functions/v1`;
      const response = await fetch(`${functionsUrl}/news-admin-manage?module=analysis&action=get_templates`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        }
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          setTemplates(result.data || []);
        }
      }
    } catch (error) {
      console.error('获取模板列表失败:', error);
    }
  };

  // 获取分析详情（编辑模式）
  const fetchAnalysis = async () => {
    if (!isEditing) return;
    
    try {
      // 获取当前用户的 session token
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      if (sessionError || !session?.access_token) {
        console.error('获取 session 失败:', sessionError);
        return;
      }

      // 调用 Edge Function 获取分析详情
      const functionsUrl = API_CONFIG.FUNCTIONS_URL || `${import.meta.env.VITE_SUPABASE_URL}/functions/v1`;
      const response = await fetch(`${functionsUrl}/news-admin-manage?module=analysis&action=get_analysis&id=${id}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        }
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          const data = result.data;
          setAnalysis(data);
          setFormData({
            article_id: data.article_id,
            template_id: data.template_id,
            content_json: typeof data.content_json === 'string' ? data.content_json : JSON.stringify(data.content_json, null, 2),
            full_article: data.full_article || '',
            slug: data.slug || '',
            status: data.status
          });
        }
      } else {
        throw new Error('获取分析详情失败');
      }
    } catch (error) {
      console.error('获取分析详情失败:', error);
      alert('获取分析详情失败，请重试');
      navigate('/admin/insights');
    }
  };

  // 生成slug
  const generateSlugFromTitle = async () => {
    if (!formData.article_id) {
      alert('请先选择文章');
      return;
    }

    try {
      const selectedArticle = articles.find(a => a.id === formData.article_id);
      if (!selectedArticle) return;

      // 使用正确的URL构建方式
      const functionsUrl = API_CONFIG.FUNCTIONS_URL || `${import.meta.env.VITE_SUPABASE_URL}/functions/v1`;
      const response = await fetch(`${functionsUrl}/aimodule-content-admin-generate-news`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          type: 'slug',
          parameters: {
            title: selectedArticle.title,
            content: selectedArticle.content
          }
        })
      });

      const result = await response.json();
      if (response.ok && result.success) {
        setFormData({ ...formData, slug: result.data.slug });
      } else {
        alert('生成slug失败：' + (result.error || '未知错误'));
      }
    } catch (error) {
      console.error('生成slug失败:', error);
      alert('生成slug失败，请重试');
    }
  };

  // 生成模板内容
  const generateTemplateContent = () => {
    if (!formData.template_id) {
      alert('请先选择模板');
      return;
    }

    const selectedTemplate = templates.find(t => t.id === formData.template_id);
    if (!selectedTemplate) return;

    const templateContent = {
      title: `基于${selectedTemplate.name}的深度分析`,
      summary: '请填写分析摘要',
      sections: [
        {
          id: 1,
          title: '背景分析',
          content: '请填写背景分析内容',
          order: 1
        },
        {
          id: 2,
          title: '深度解读',
          content: '请填写深度解读内容',
          order: 2
        },
        {
          id: 3,
          title: '影响评估',
          content: '请填写影响评估内容',
          order: 3
        },
        {
          id: 4,
          title: '趋势预测',
          content: '请填写趋势预测内容',
          order: 4
        }
      ],
      category: '深度分析',
      tags: ['分析', '洞察'],
      wordCount: 0
    };

    setFormData({ ...formData, content_json: JSON.stringify(templateContent, null, 2) });
  };

  // 自动保存函数
  const autoSave = async (data: FormData) => {
    if (!isEditing || !data.article_id || !data.template_id || skipAutoSave) {
      return;
    }

    console.log('🔄 执行自动保存:', { id, data })

    // 验证JSON格式
    try {
      if (typeof data.content_json === 'string') {
        JSON.parse(data.content_json);
      } else if (typeof data.content_json === 'object') {
        // 如果已经是对象，直接使用
        console.log('content_json已经是对象格式，跳过JSON解析');
      } else {
        console.warn('content_json格式不正确，跳过自动保存');
        return;
      }
    } catch (error) {
      console.warn('JSON格式不正确，跳过自动保存:', error);
      return;
    }

    setAutoSaving(true);
    
    try {
      const submitData = {
        article_id: data.article_id,
        template_id: data.template_id,
        content_json: typeof data.content_json === 'string'
          ? JSON.parse(data.content_json)
          : data.content_json,
        full_article: data.full_article || null,
        slug: data.slug || null,
        status: data.status
      };

      // 获取当前用户的 session token
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      if (sessionError || !session?.access_token) {
        console.error('获取 session 失败:', sessionError);
        return;
      }

      // 调用 Edge Function 更新分析
      const functionsUrl = API_CONFIG.FUNCTIONS_URL || `${import.meta.env.VITE_SUPABASE_URL}/functions/v1`;
      const response = await fetch(`${functionsUrl}/news-admin-manage?module=analysis&action=update_analysis`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id,
          updateData: submitData
        })
      });

      if (!response.ok) {
        throw new Error('更新分析失败');
      }
      setLastSaved(new Date());
    } catch (error) {
      console.error('自动保存失败:', error);
    } finally {
      setAutoSaving(false);
    }
  };

  // 手动保存（用于创建新分析）
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.article_id || !formData.template_id || !formData.content_json.trim()) {
      alert('请填写必填字段');
      return;
    }

    // 验证JSON格式
    try {
      if (typeof formData.content_json === 'string') {
        JSON.parse(formData.content_json);
      } else if (typeof formData.content_json !== 'object') {
        alert('分析内容格式不正确，请检查');
        return;
      }
    } catch (error) {
      alert('分析内容JSON格式不正确，请检查');
      return;
    }

    setSubmitting(true);
    
    try {
      const submitData = {
        article_id: formData.article_id,
        template_id: formData.template_id,
        content_json: typeof formData.content_json === 'string'
          ? JSON.parse(formData.content_json)
          : formData.content_json,
        full_article: formData.full_article || null,
        slug: formData.slug || null,
        status: formData.status
      };

      if (isEditing) {
        await autoSave(formData);
        alert('保存成功！');
      } else {
        // 获取当前用户的 session token
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();
        if (sessionError || !session?.access_token) {
          console.error('获取 session 失败:', sessionError);
          return;
        }

        // 调用 Edge Function 创建分析
        const functionsUrl = API_CONFIG.FUNCTIONS_URL || `${import.meta.env.VITE_SUPABASE_URL}/functions/v1`;
        const response = await fetch(`${functionsUrl}/news-admin-manage?module=analysis&action=create_analysis`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            analysisData: submitData
          })
        });

        if (!response.ok) {
          throw new Error('创建分析失败');
        }
        alert('创建成功！');
        navigate('/admin/insights');
      }
    } catch (error) {
      console.error('保存失败:', error);
      alert('保存失败，请重试');
    } finally {
      setSubmitting(false);
    }
  };

  // 预览功能
  const handlePreview = () => {
    if (analysis?.slug) {
      window.open(`/official-analysis/${analysis.slug}`, '_blank');
    } else if (analysis?.id) {
      window.open(`/official-analysis/${analysis.id}`, '_blank');
    }
  };

  // HTML内容过滤函数，按照CLAUDE.md指南处理AI生成的标签
  const filterHTMLContent = (content: string): string => {
    if (!content) return '';
    
    // 检查是否存在AI生成的特殊标签
    if (content.includes('===HTML_RESULT_START===') && content.includes('===HTML_RESULT_END===')) {
      // 提取HTML_RESULT区域的内容
      const htmlStartIndex = content.indexOf('===HTML_RESULT_START===') + '===HTML_RESULT_START==='.length;
      const htmlEndIndex = content.indexOf('===HTML_RESULT_END===');
      if (htmlEndIndex > htmlStartIndex) {
        return content.substring(htmlStartIndex, htmlEndIndex).trim();
      }
    }
    
    // 如果没有特殊标签，移除可能存在的THINKING区域
    let filteredContent = content;
    if (content.includes('===THINKING_START===') && content.includes('===THINKING_END===')) {
      filteredContent = content.replace(/===THINKING_START===[\s\S]*?===THINKING_END===/g, '').trim();
    }
    
    return filteredContent;
  };

  // AI重新生成内容
  const handleAIRegenerate = async () => {
    if (!isEditing || !analysis) {
      alert('只能对已存在的分析进行AI重新生成');
      return;
    }

    // 防止重复点击
    if (regenerating) {
      console.warn('AI重新生成正在进行中，忽略重复请求');
      return;
    }

    if (!confirm('确定要使用AI重新生成分析内容吗？系统会自动检测并更新现有分析。')) {
      return;
    }

    // 验证必要参数
    if (!analysis.article_id || !analysis.template_id) {
      alert('分析数据不完整，无法进行AI重新生成');
      return;
    }

    setRegenerating(true);
    
    try {
      console.log('🔧 调试信息:', {
        VITE_SUPABASE_URL: import.meta.env.VITE_SUPABASE_URL,
        VITE_SUPABASE_FUNCTIONS_URL: API_CONFIG.FUNCTIONS_URL,
        构建的URL: `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/aimodule-content-admin-generate-news`
      });
      
      // 使用正确的URL构建方式
      const functionsUrl = API_CONFIG.FUNCTIONS_URL || `${import.meta.env.VITE_SUPABASE_URL}/functions/v1`;
      const fullUrl = `${functionsUrl}/aimodule-content-admin-generate-news`;
      
      console.log('📡 发送请求到:', fullUrl);
      
      const response = await fetch(fullUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          action: 'generate',
          type: 'official_analysis',
          parameters: {
            articleId: analysis.article_id.toString(),
            templateId: analysis.template_id.toString(),
            auto_publish: false
          }
        })
      });

      const result = await response.json();
      console.log('🔍 AI生成响应:', result);

      if (response.ok && result.success) {
        // 处理响应数据 - 注意API返回的是嵌套结构 result.data.analysis
        const actualData = result.data?.analysis || result.data;
        const operationType = result.data?.operation || result.operation || 'update';
        
        // 确保 actualData 存在且包含必要字段
        if (!actualData) {
          console.error('❌ API响应数据格式异常:', result);
          alert('AI生成失败：响应数据格式异常');
          return;
        }
        
        console.log('📝 API返回的分析数据:', {
          actualData,
          operationType,
          content_json_type: typeof actualData.content_json,
          full_article_length: actualData.full_article?.length || 0,
        });
        
        // 重新获取最新的分析数据，确保显示最新内容
        console.log('🔄 重新获取分析数据以确保同步...');
        await fetchAnalysis();
        
        // 显示成功消息
        const actionText = operationType === 'create' ? '创建' : '重新生成';
        alert(`AI${actionText}成功！页面已更新为最新内容。`);
      } else {
        console.error('❌ AI生成失败:', result);
        const errorMsg = result.error || result.message || '未知错误';
        alert('AI生成失败：' + errorMsg);
      }
    } catch (error) {
      console.error('❌ AI重新生成异常:', error);
      const errorMsg = error instanceof Error ? error.message : '网络错误，请重试';
      alert('AI重新生成失败：' + errorMsg);
    } finally {
      setRegenerating(false);
    }
  };

  // 自动保存的防抖效果 - 已禁用
  // useEffect(() => {
  //   if (!isEditing || skipAutoSave) return;
  //   
  //   const timeoutId = setTimeout(() => {
  //     autoSave(formData);
  //   }, 2000); // 2秒后自动保存

  //   return () => clearTimeout(timeoutId);
  // }, [formData, isEditing, skipAutoSave]);

  useEffect(() => {
    const initData = async () => {
      setLoading(true);
      await Promise.all([
        fetchArticles(),
        fetchTemplates(),
        fetchAnalysis()
      ]);
      setLoading(false);
    };
    
    initData();
  }, [id]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* 页面头部 */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate('/admin/insights')}
              className="flex items-center text-gray-600 hover:text-gray-900 transition-colors"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              返回列表
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                {isEditing ? '编辑官方深度分析' : '创建官方深度分析'}
              </h1>
              {isEditing && analysis && (
                <p className="text-gray-600 mt-1">
                  编辑ID: {analysis.id} | 创建时间: {new Date(analysis.created_at).toLocaleString()}
                </p>
              )}
            </div>
          </div>
          
          {isEditing && analysis && (
            <div className="flex items-center space-x-3">
              <div className="flex items-center space-x-2 text-sm text-gray-500">
                {autoSaving && (
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
                    <span>自动保存中...</span>
                  </div>
                )}
                {lastSaved && !autoSaving && (
                  <span>已保存于 {lastSaved.toLocaleTimeString()}</span>
                )}
              </div>
              <button
                onClick={handlePreview}
                className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
              >
                <Eye className="w-4 h-4 mr-2" />
                预览
              </button>
              <button
                onClick={handleAIRegenerate}
                disabled={regenerating}
                className="flex items-center px-4 py-2 text-purple-600 bg-purple-50 rounded-lg hover:bg-purple-100 disabled:opacity-50 transition-colors"
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${regenerating ? 'animate-spin' : ''}`} />
                {regenerating ? 'AI生成中...' : 'AI重新生成'}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Tab导航 */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8 px-6">
            <button
              type="button"
              onClick={() => setActiveTab('basic')}
              className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center ${
                activeTab === 'basic'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Settings className="w-4 h-4 mr-2" />
              基本信息与结构化内容
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('article')}
              className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center ${
                activeTab === 'article'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <FileText className="w-4 h-4 mr-2" />
              完整文章内容
            </button>
          </nav>
        </div>
      </div>

      {/* 编辑表单 */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="p-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* 基本信息与结构化内容 Tab */}
            {activeTab === 'basic' && (
              <div className="space-y-6">
                {/* 基本信息 */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      选择文章 *
                    </label>
                    <select
                      required
                      value={formData.article_id}
                      onChange={(e) => setFormData({ ...formData, article_id: parseInt(e.target.value) })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                    >
                      <option value={0}>请选择文章</option>
                      {articles.map((article) => (
                        <option key={article.id} value={article.id}>
                          {article.title}
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      选择模板 *
                    </label>
                    <select
                      required
                      value={formData.template_id}
                      onChange={(e) => setFormData({ ...formData, template_id: parseInt(e.target.value) })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                    >
                      <option value={0}>请选择模板</option>
                      {templates.map((template) => (
                        <option key={template.id} value={template.id}>
                          {template.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      状态
                    </label>
                    <select
                      value={formData.status}
                      onChange={(e) => setFormData({ ...formData, status: parseInt(e.target.value) })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                    >
                      <option value={0}>草稿</option>
                      <option value={1}>已发布</option>
                    </select>
                  </div>
                </div>

                {/* SEO Slug */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    SEO友好的URL标识 (slug) *
                  </label>
                  <div className="flex gap-3">
                    <input
                      type="text"
                      required
                      value={formData.slug}
                      onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                      placeholder="例如: digital-transformation-analysis"
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                    <button
                      type="button"
                      onClick={generateSlugFromTitle}
                      disabled={!formData.article_id}
                      className="px-4 py-2 bg-green-100 text-green-800 rounded-md hover:bg-green-200 disabled:opacity-50 transition-colors flex items-center"
                      title="根据文章标题自动生成英文slug"
                    >
                      <Sparkles className="w-4 h-4 mr-1" />
                      自动生成
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    用于SEO友好的URL，建议使用有意义的英文单词，用连字符分隔
                  </p>
                </div>

                {/* 分析内容 */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-medium text-gray-700">
                      分析内容 (JSON格式) *
                    </label>
                    <button
                      type="button"
                      onClick={generateTemplateContent}
                      disabled={!formData.template_id}
                      className="text-sm px-3 py-1 bg-blue-100 text-blue-800 rounded-md hover:bg-blue-200 disabled:opacity-50 transition-colors flex items-center"
                    >
                      <Sparkles className="w-4 h-4 mr-1" />
                      生成模板结构
                    </button>
                  </div>
                  <textarea
                    required
                    value={formData.content_json}
                    onChange={(e) => setFormData({ ...formData, content_json: e.target.value })}
                    rows={25}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono text-sm"
                    placeholder='请输入JSON格式的分析内容，例如：\n{\n  "sections": [\n    {\n      "title": "分析标题",\n      "content": "分析内容"\n    }\n  ]\n}'
                  />
                  <div className="mt-2 p-3 bg-blue-50 rounded-md">
                    <p className="text-sm text-blue-800">
                      <strong>提示：</strong>这里配置的是结构化的分析内容，用于页面展示的各个分析模块。如需编辑完整的文章内容，请切换到"完整文章内容"标签页。
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* 完整文章内容 Tab */}
            {activeTab === 'article' && (
              <div className="space-y-6">
                <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4 mb-6">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <FileText className="h-5 w-5 text-yellow-400" />
                    </div>
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-yellow-800">
                        完整文章内容编辑
                      </h3>
                      <div className="mt-2 text-sm text-yellow-700">
                        <p>这里可以编辑完整的文章内容，支持Markdown格式。此内容将作为文章的详细版本展示给用户。</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    完整文章内容
                  </label>
                  <textarea
                    value={formData.full_article}
                    onChange={(e) => setFormData({ ...formData, full_article: e.target.value })}
                    rows={30}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="请输入完整的分析文章内容（可选）\n\n支持Markdown格式，例如：\n\n# 标题\n\n## 二级标题\n\n**粗体文本**\n\n*斜体文本*\n\n- 列表项1\n- 列表项2\n\n> 引用文本\n\n```\n代码块\n```"
                  />
                  <div className="mt-2 flex justify-between items-center text-sm text-gray-500">
                    <span>字符数: {formData.full_article.length}</span>
                    <span>预计阅读时间: {Math.ceil(formData.full_article.length / 300)} 分钟</span>
                  </div>
                </div>

                {/* 文章预览区域 */}
                {formData.full_article && (
                  <div className="border-t pt-6">
                    <h3 className="text-lg font-medium text-gray-900 mb-4">内容预览</h3>
                    <div className="bg-gray-50 rounded-md p-4 max-h-96 overflow-y-auto">
                      {/* 检查内容是否为HTML格式 */}
                      {/<[^>]+>/.test(filterHTMLContent(formData.full_article)) ? (
                        <div 
                          className="prose prose-sm max-w-none"
                          dangerouslySetInnerHTML={{ 
                            __html: filterHTMLContent(formData.full_article) 
                          }}
                        />
                      ) : (
                        <div className="prose prose-sm max-w-none">
                          <pre className="whitespace-pre-wrap text-sm text-gray-700">
                            {filterHTMLContent(formData.full_article)}
                          </pre>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* 操作按钮 */}
            <div className="flex justify-between items-center pt-6 border-t border-gray-200">
              <div>
                {isEditing && (
                  <div className="flex items-center space-x-4">
                    <button
                      type="button"
                      onClick={handleAIRegenerate}
                      disabled={regenerating}
                      className="flex items-center px-4 py-2 text-purple-600 bg-purple-50 rounded-md hover:bg-purple-100 disabled:opacity-50 transition-colors"
                    >
                      <RefreshCw className={`w-4 h-4 mr-2 ${regenerating ? 'animate-spin' : ''}`} />
                      {regenerating ? 'AI生成中...' : 'AI重新生成'}
                    </button>
                    <div className="text-sm text-gray-500">
                      {autoSaving && (
                        <div className="flex items-center">
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
                          <span>自动保存中...</span>
                        </div>
                      )}
                      {lastSaved && !autoSaving && (
                        <span>✓ 已自动保存于 {lastSaved.toLocaleTimeString()}</span>
                      )}
                    </div>
                  </div>
                )}
              </div>
              <div className="flex space-x-4">
                <button
                  type="button"
                  onClick={() => navigate('/admin/insights')}
                  className="px-6 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
                >
                  返回列表
                </button>
                {!isEditing && (
                  <button
                    type="submit"
                    disabled={submitting}
                    className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 transition-colors flex items-center"
                  >
                    <Save className="w-4 h-4 mr-2" />
                    {submitting ? '创建中...' : '创建分析'}
                  </button>
                )}
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default OfficialAnalysisEdit;